import jsPDF from 'jspdf';

const normalizeDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const createFranchiseAgreementPdfDocument = (formData = {}, options = {}) => {
  if (!formData) {
    throw new Error('Form data is required to generate the franchise agreement PDF.');
  }

  const createdAt =
    normalizeDate(options.createdAt) ||
    normalizeDate(formData.agreementCreatedAt) ||
    normalizeDate(formData.createdAt) ||
    new Date();
  const day = createdAt.getDate();
  const month = createdAt.toLocaleDateString('en-US', { month: 'long' });
  const year = createdAt.getFullYear();

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - (2 * margin);
  let y = 20;

  // Helper function to add text with automatic page breaks
  const addText = (text, fontSize = 11, fontStyle = 'normal', addSpace = 7) => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);
    const lines = doc.splitTextToSize(text, maxWidth);
    
    lines.forEach(line => {
      if (y > pageHeight - 30) {
        doc.addPage();
        y = 20;
      }
      doc.text(line, margin, y);
      y += addSpace;
    });
  };

  const addRichText = (segments, fontSize = 11, addSpace = 7) => {
    doc.setFontSize(fontSize);
    let x = margin;
    const maxX = margin + maxWidth;

    const ensurePage = () => {
      if (y > pageHeight - 30) {
        doc.addPage();
        y = 20;
      }
    };

    segments.forEach(({ text, bold }) => {
      const parts = String(text || '').split(/(\s+)/);
      parts.forEach((part) => {
        if (!part) return;
        const isSpace = /^\s+$/.test(part);
        const width = doc.getTextWidth(part);
        if (!isSpace && x + width > maxX) {
          y += addSpace;
          x = margin;
          ensurePage();
        } else if (isSpace && x === margin) {
          return;
        }
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        if (!isSpace) {
          doc.text(part, x, y);
        }
        x += width;
      });
    });

    y += addSpace;
  };

  const addClause = (label, text, fontSize = 11, addSpace = 7) => {
    addRichText(
      [
        { text: `${label} `, bold: true },
        { text, bold: false },
      ],
      fontSize,
      addSpace,
    );
  };

  const addSection = (title, fontSize = 12) => {
    if (y > pageHeight - 40) {
      doc.addPage();
      y = 20;
    }
    y += 5;
    addText(title, fontSize, 'bold', 10);
  };

    // Title (centered)
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('FRANCHISE AGREEMENT', pageWidth / 2, 20, { align: 'center' });
  y = 35;

  addRichText([
    { text: 'This Franchise Agreement ("Agreement") is made and entered into on this ', bold: false },
    { text: String(day), bold: true },
    { text: ' day of ', bold: false },
    { text: String(month), bold: true },
    { text: ', ', bold: false },
    { text: String(year), bold: true },
    { text: '.', bold: false },
  ]);

  addSection('BY AND BETWEEN:');
  addRichText([
    { text: 'Steadeesoft AI Technologies Pvt Ltd', bold: true },
    { text: ', a company incorporated under the Companies Act, 2013, having its registered office at No 74-1-3363/a-4-13, HAL 2nd Stage, Bengaluru, 560008, Bangalore, Karnataka, India (hereinafter referred to as the "Franchisor", which term shall include its successors and assigns).', bold: false },
  ]);
  addText('AND', 11, 'bold', 10);

  const entityType = formData.entityType === 'individual' ? 'an individual' : 'an entity';
  const officeType = formData.entityType === 'individual' ? 'residence' : 'registered office';
  const addressSegments = [
    formData.addressLine1 || '[Address]',
    formData.addressLine2,
    formData.city || '[City]',
    formData.state || '[State]',
  ].filter(Boolean);
  const addressText = addressSegments.length
    ? `${addressSegments.join(', ')}${formData.pincode ? ` - ${formData.pincode}` : ''}, India`
    : 'India';
  const franchiseeName = formData.fullName || '[Insert Franchisee Name]';
  const reraNumber = formData.reraNumber || '_____________';
  const hasActiveRera = String(formData.holdActiveRera || '').toLowerCase() === 'yes';
  const hasLegacyReraDetails =
    !formData.holdActiveRera &&
    !formData.arrangeReraIn90Days &&
    Boolean(String(formData.reraNumber || '').trim());
  const shouldShowReraClause = hasActiveRera || hasLegacyReraDetails;
  const franchiseeIntroSegments = [
    { text: franchiseeName, bold: true },
    { text: `, ${entityType} having its ${officeType} at `, bold: false },
    { text: addressText, bold: true },
  ];
  if (shouldShowReraClause) {
    franchiseeIntroSegments.push({ text: ', holding a valid RERA registration number ', bold: false });
    franchiseeIntroSegments.push({ text: reraNumber, bold: true });
  }
  franchiseeIntroSegments.push({
    text: ', (hereinafter referred to as the "Franchisee", which term shall include its successors, permitted assigns and legal representatives).',
    bold: false,
  });
  addRichText(franchiseeIntroSegments);

  addSection('WHEREAS');
  addSection('DEFINITIONS');
  addText('Unless otherwise stated, the following terms shall have the meanings ascribed below:');
  addClause('1.1', '"Brand" means Douglas & McCartney, owned by the Brand Licensor and licensed to the Franchisor.');
  addClause('1.2', '"Franchise Fee" means a non refundable fee of Rs. 1,00,000 plus GST of Rs. 18,000 (total Rs. 1,18,000) payable by the Franchisee.');
  addClause('1.3', '"Territory" means the Republic of India.');
  addClause('1.4', '"Effective Date" means the date of execution of this Agreement.');
  addClause('1.5', '"Franchise Term" means 5 (five) years from the Effective Date.');
  addClause('1.6', '"System" includes methods of operation, marketing techniques, training materials, and other intellectual property associated with the Brand.');

  y += 5;
  addClause('A.', 'The brand name Douglas & McCartney TM is licensed to the Franchisor by Steadee Soft AI Technologies LLC FZ, a company registered in Dubai, UAE (hereinafter referred to as the "Brand Licensor"), and is used in connection with real estate software & business services;');
  addClause('B.', 'The Franchisor has developed a system, trade name, branding, and operational framework associated with real estate services under the Douglas & McCartney brand;');
  addClause('C.', "The Franchisee is desirous of conducting real estate business in India using the Franchisor's brand, tools, and system, and maintaining their own independent RERA license and legal entity;");
  addClause('D.', 'The Franchisor is willing to grant a non-exclusive, non-transferable, limited franchise for a term of 5 (five) years in accordance with the terms set out herein.');

  addSection('GRANT OF FRANCHISE');
  addClause('2.1 License Grant:', 'The Franchisor hereby grants to the Franchisee a limited, non-exclusive, non-transferable, non-sublicensable right to operate a real estate business under the Douglas & McCartney brand within India for the Franchise Term.');
  addClause('2.2 Scope of Use:', 'The Franchisee shall operate independently using its own legal entity, RERA registration, and billing mechanisms, and shall not hold itself out as an agent or employee of the Franchisor. In case the Franchisee does not have an active RERA registration, the Franchisee shall endeavor to become compliant within 90 days of signing this document. If this compliance is not met, the Franchisor can and will terminate this Agreement by giving prior written notice. No refund would be applicable.');

  addSection('RIGHTS, TERM AND FEES');
  addText('1. THE PREAMBLE');
  addText('"Douglas & McCartney" is the intellectual property of SteadeeSoft AI Technologies Pvt Ltd which holds all the intellectual property rights like trademark, design, name, etc.');
  addText('The Franchisor is engaged in the business of real estate education & training pertaining to enabling individuals to upscale their skills to become successful in their endeavours of real estate consulting entrepreneurship. The Franchisor enables the Franchisee to plug into the ecosystem of branding, training and networking in real estate consulting in accordance with a distinctive system and standard operating procedures, utilizing and comprising certain proprietary marks and confidential information.');
  addText("The Franchisee desires to obtain the benefit of Douglas & McCartney's goodwill, intellectual property and operations know-how, for the purpose of operating an efficient real estate consulting business.");
  addText('The Franchisor through its own effort has established substantial reputation and goodwill in the Business and the Franchisee recognizes the benefit to be derived therefrom and acknowledges the necessity of conforming to the high standards and uniform specifications of the Business.');

  addSection('2. FRANCHISE RIGHTS');
  addText('During the tenure of the Franchise Agreement, the Franchisee is granted:');
  addClause('a)', 'The exclusive rights to operate in the field of real estate consulting using the brand name, Douglas & McCartney.');
  addClause('b)', "The exclusive right to use the Franchisor's or its related entity's intellectual property and operations know how including but not limited to its trade name, marks and copyrights, strictly for the conduct of the Business.");

  addSection('3. TERM AND RENEWAL');
  addClause('3.1', 'This Agreement shall remain in force for a period of 5 (five) years from the Effective Date.');
  addClause('3.2', 'The Agreement may be renewed for additional terms upon mutual agreement separately drawn in writing, subject to terms and payment of prevailing applicable fees at the time of renewal.');

  addSection('4. INITIAL FRANCHISE FEE');
  addText('The franchise fee payable by the Franchisee shall comprise of an up-front, non refundable amount of INR 1,00,000 plus GST of INR 18,000 (18%), totaling INR 1,18,000.');

  addSection('5. ONGOING FEES/ROYALTY');
  addText('The Franchisee shall pay to the Franchisor 0% (ZERO %) of its monthly network sales revenue.');

  addSection('6. FRANCHISOR OBLIGATIONS');
  addText('The Franchisor shall:');
  addText('- Allow use of brand name for conducting real estate business.');
  addText('- Provide access to online training via scheduled video conferencing;');
  addText('- Allow the use of an official Douglas & McCartney email address allocated specifically to the franchisee;');
  addText('- Offer branding guidelines, templates, and operational assistance as may be reasonably required;');
  addText('- Offer branded merchandise, customised merchandise, business card templates, early access to mandate projects, reading material.');

  addSection('7. FRANCHISEE OBLIGATIONS');
  addClause('7.1', 'The Franchisee shall:');
  addText('- Maintain a valid RERA registration for the duration of this Agreement;');
  addText('- Conduct business in a professional and ethical manner;');
  addText('- Use the Brand solely for real estate activities;');
  addText('- Maintain confidentiality of the training provided safeguarding intellectual property rights;');
  addText('- Comply with all applicable laws and regulations;');
  addText('- Keep all customer and transactional records confidential.');
  addClause('7.2', 'The Franchisee shall be solely responsible for its own taxes, employment matters, regulatory filings, and all operational liabilities.');
  addClause('7.3', "The Franchisor shall not be liable for the Franchisee's operations, representations, or third-party claims arising out of the Franchisee's independent activities.");

  addSection('8. TERMINATION');
  addText('The Franchisor may terminate the franchisee agreement for any material breach committed by the franchisee by providing a show cause notice of 15 days for which the franchisor provides the curative remedy rectifying the breach or provide sufficient cause for such lapse that has transpired to a breach within 15 days of such communication.');
  addText('If the franchisee has failed to comply to the statutory requirements of the government as per the law prevailing from time to time.');
  addText('The Franchisor may forthwith terminate the Franchisee if he is convicted for any criminal offence and is in the opinion of the Franchisor guilty of gross moral turpitude or crime of financial nature.');
  addText('Any action detrimental to the interests of the brand shall invoke instant termination subject to the show cause provision provided in this agreement.');

  addSection('9. CONSEQUENCES TO TERMINATION');
  addText('The franchisee ceases to use or transact the proprietary intellectual property including the material provided by the franchisor and shall discontinue to transact in the name of the franchisor.');
  addText('Indemnify the Franchisor of any such damage or damages that may be caused due to the mis acts or breach of the franchisee not limiting to himself/herself or he being vicariously liable for the conduct of his agents and employees.');

  addSection('10. PROPRIETARY MARKS');
  addText('The Franchisor or its related company will be responsible for the protection of the Douglas & McCartney TM trademarks. The Franchisor shall grant to the Franchisee a royalty-free, non-assignable right to use the Douglas & McCartney trademark for use in relation to the Franchise, which right shall be exclusive for real estate.');
  addText('The Franchisee acknowledges that these marks belong to the Franchisor or its related company, and will not willfully infringe them, register ownership of them, or contest the ownership and rights of the Franchisor or its related company. The Franchisee will give its fullest cooperation in protecting the Douglas & McCartney trademarks, intellectual property, and goodwill.');

  addSection('11. NON-COMPETITION');
  addText("The Franchisee and his/her immediate families shall not engage directly or indirectly in any activity that may be prejudicial or competes or imitates the franchisor's business model, during the term of the Franchise Agreement and for five (5) years thereafter.");

  addSection('12. CONFIDENTIALITY');
  addClause('12.1', 'Not to reveal to external parties confidential information including but not limited to those contained in the operations documentation, business plans, internal instruction circulars, correspondence and any other materials marked as confidential.');
  addClause('12.2', 'Each party agrees to maintain in strict confidence any confidential information received during the course of this Agreement.');
  addClause('12.3', 'This obligation shall survive for 2 (two) years post-termination of the Agreement.');

  addSection('13. INTELLECTUAL PROPERTY');
  addClause('13.1', 'The Franchisee acknowledges that all intellectual property rights in the Brand and System are owned by the Franchisor.');
  addClause('13.2', 'The Franchisee shall not use the Brand or any derivative thereof except as permitted under this Agreement.');
  addClause('13.3', 'Upon termination or expiration, the Franchisee shall immediately cease using the Brand and related materials.');

  addSection('14. GOVERNMENT REGULATIONS');
  addText('The Franchisee to comply with all government regulations and licensing requirements governing the conduct of his/her operations.');

  addSection('15. RENEWAL');
  addText('To notify the Franchisor in writing not less than three (3) months before the expiration of the initial franchise term if it intends to exercise the option for extension of the term. The applicable cost of renewal is subjective, at the discretion of the Franchisor, the Franchisee having complied to the stipulated agreements and guidelines laid upon by the Franchisor without any breach or deviations and will be extended upon mutual terms under a separate agreement.');

  addSection('16. OTHER TERMS');
  addSection('LAW AND ARBITRATION', 11);
  addText('All rights and obligations of the parties under this Franchise Agreement shall be governed and construed in accordance with the laws of India.');
  addText('Arbitration in Bangalore shall be the forum to resolve disputes, with a single arbitrator being appointed by the parties mutually.');

  y += 10;
  addText('IN WITNESS WHEREOF, the parties have duly executed these Heads of Agreement as of the date set forth above.', 11, 'bold');

  y += 15;
  addText('SIGNED By :', 11, 'bold');
  addText('__________________________________)', 11, 'bold');
  addText('For and on behalf of )');
  addText('STEADEE Soft AI Technologies Pvt Ltd', 11, 'bold');
  addText('In the presence of ______________________________ )');
  addText('SIGNED by :', 11, 'bold');
  addText(formData.fullName || '____________________', 11, 'bold');
  addText('For and on behalf of : ____________________');
  addText('In the presence of ______________________________)');


  // Add footer to all pages
  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(`Douglas & McCartney Franchise Agreement - Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  // Save the PDF
  return doc;
};

export const generateFranchiseAgreementPdfBase64 = (formData = {}, options = {}) => {
  const doc = createFranchiseAgreementPdfDocument(formData, options);
  const dataUri = doc.output('datauristring');
  const parts = dataUri.split(',');
  return parts.length > 1 ? parts[1] : dataUri;
};
